// @ts-check
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { useNavigate, useParams } from 'react-router-dom';

import { useProjectsListContext } from '@datahooks/ProjectsListContext';
import useBusinessViewOverview from '@datahooks/useBusinessViewOverview';

import LoadingIcon from '@components/navigation/LoadingIcon';
import { TestableCard, TestableText } from '@components/general/TestableComponents';
import { ErrorCard, CARD_TYPES } from '@components/cards/ErrorCard';

import BusinessViewHeader from '../shared/BusinessViewHeader';
import BusinessViewProjectSummary from '../shared/BusinessViewProjectSummary';
import BusinessViewKpiSection from './BusinessViewKpiSection';
import BusinessViewFlowStatusTrend from './BusinessViewFlowStatusTrend';
import BusinessViewFlowsTable from './BusinessViewFlowsTable';
import { buildProjectOptions } from '../shared/businessViewProjectMapper';
import { mapOverviewToBusinessView } from './businessViewMapper';
import { buildOverviewRoute } from '../shared/businessViewConfig';

/**
 * Top-level Business View reporting component: header, project summary, KPI row, trend
 * chart and flows table.
 *
 * The project picker uses ProjectsListContext's *filtered* list, so it mirrors whatever the
 * user has narrowed the menu to. Passing `data` bypasses the fetch, for tests and previews.
 *
 * @param {Object} props
 * @param {import('../shared/businessView.types').BusinessViewData} [props.data]
 * @returns {JSX.Element}
 */
const BusinessView = ({ data }) => {
  const { projectId: routeProjectId } = useParams();
  const navigate = useNavigate();

  const {
    filteredFlatProjects = [],
    filters = [],
    isLoading: projectsLoading,
  } = useProjectsListContext();

  const projectOptions = useMemo(() => buildProjectOptions(filteredFlatProjects), [filteredFlatProjects]);

	const [selectedProjectId, setSelectedProjectId] = useState(routeProjectId ?? '');

  // The selected project lives in the URL so the page can be linked to, and so
  // returning from a flow's detail view lands back on the project it belongs to.
  //
  // Keep the selection valid: default to the first project, and re-default if the
  // one in the URL is not one the user can see — which also covers it dropping out
  // of the filtered list when the filters change.
  useEffect(() => {
    if (projectOptions.length === 0) {
      return;
    }

    const nextId = projectOptions.some((option) => option.id === routeProjectId)
      ? String(routeProjectId)
      : projectOptions[0].id;

    setSelectedProjectId(nextId);

    if (nextId !== routeProjectId) {
      // Replaced rather than pushed: defaulting the project is not a navigation
      // the user made, so it must not need a second Back press to leave.
      navigate(buildOverviewRoute(nextId), { replace: true });
    }
  }, [projectOptions, routeProjectId, navigate]);

	//Load business overview data for the selected project. If `data` is passed, skip the fetch and use that instead.
  const { overview, hasNoData, isLoading: overviewLoading, error } = useBusinessViewOverview(data ? null : selectedProjectId);

  const selectedProject = useMemo(
    () => filteredFlatProjects.find((/** @type {any} */ project) => String(project.projectId) === selectedProjectId),
    [filteredFlatProjects, selectedProjectId]
  );

  const viewData = useMemo(
    () => data ?? mapOverviewToBusinessView(overview, selectedProject),
    [data, overview, selectedProject]
  );

	const isPartial = viewData.flows.some((flow) => flow.calculationStatus === 'partial');
  const hasProjects = projectOptions.length > 0;

  // The trailing clause covers the render between the project list arriving and
  // the effect above choosing a default, which would otherwise flash "no flows".
  const isLoading = projectsLoading || overviewLoading || (!data && hasProjects && !selectedProjectId);

  /** The body below the header: error, loading, empty or the report itself. */
  let body;

  if (error) {
    body = (
      <TestableCard dataTestId="bv-error-card" className="mt-5 w-full p-4 text-center text-blue-500">
        <ErrorCard cardType={CARD_TYPES.ERROR} />
        <TestableText dataTestId="bv-error-title" className="mt-10">An error occurred while fetching the Business View data.</TestableText>
        <TestableText dataTestId="bv-error-detail" className="mt-5">Please try again later.</TestableText>
      </TestableCard>
    );
  } else if (isLoading) {
    body = (
      <div data-testid="bv-loading" className="flex w-full justify-center py-16">
        <LoadingIcon size={100} showText />
      </div>
    );
  } else if (!hasProjects) {
    body = (
      <div data-testid="bv-no-projects" className="mt-5 w-full p-4 text-center text-blue-500">
        <ErrorCard cardType={CARD_TYPES.EMPTY} />
        <TestableText dataTestId="bv-no-projects-title" variant="lead" className="mt-10">
          No projects found.
        </TestableText>
        <TestableText dataTestId="bv-no-projects-detail" className="mt-5">
          {filters.length > 0
            ? 'Try adjusting your filters to see projects.'
            : 'Please contact your administrator to be given access to a project.'}
        </TestableText>
      </div>
    );
  } else if (hasNoData) {
    body = (
      <div data-testid="bv-no-data" className="mt-5 w-full p-4 text-center text-blue-500">
        <ErrorCard cardType={CARD_TYPES.EMPTY} />
        <TestableText dataTestId="bv-no-data-title" variant="lead" className="mt-10">
          No Business View data is available for
          {selectedProject?.title ? ` ${selectedProject.title}` : ' this project'}.
        </TestableText>
        {/*
          The API 404s only when no run summary has ever been persisted. Since V85 the latest run
          is always returned, so a run outside the trend window is no longer a 404 - the copy must
          not claim it is.
        */}
        <TestableText dataTestId="bv-no-data-detail" className="mt-5">
          Either no business flows have been configured for this project, or no run has completed
          since they were added. Select another project, or contact your administrator.
        </TestableText>
      </div>
    );
  } else if (viewData.flows.length === 0) {
    body = (
      <div data-testid="bv-no-workflows" className="mt-5 w-full p-4 text-center text-blue-500">
        <ErrorCard cardType={CARD_TYPES.EMPTY} />
        <TestableText dataTestId="bv-no-workflows-title" variant="lead" className="mt-10">
          No business flows have been calculated for this project yet.
        </TestableText>
        <TestableText dataTestId="bv-no-workflows-detail" className="mt-5">
          Flows appear once a run completes for a project that has a workflow configuration.
        </TestableText>
      </div>
    );
  } else {
    body = (
      <>
        <BusinessViewProjectSummary summary={viewData.summary} />

        <BusinessViewKpiSection kpis={viewData.kpis} isPartial={isPartial} />

        <BusinessViewFlowStatusTrend trend={viewData.trend} trendDays={viewData.trendDays} />

        <BusinessViewFlowsTable flows={viewData.flows} projectId={selectedProjectId} />
      </>
    );
  }

  return (
    <div data-testid="business-view" className="mx-auto flex w-full max-w-screen-4xl flex-col gap-6 py-6">
      <BusinessViewHeader
        projects={projectOptions}
        selectedProjectId={selectedProjectId}
        /* The URL owns the selection, so changing project navigates rather than setting state. */
        onProjectChange={(id) => navigate(buildOverviewRoute(id))}
      />
      {body}
    </div>
  );
};

BusinessView.propTypes = {
  data: PropTypes.object,
};

export default BusinessView;
